import {ref, inject} from 'vue'
import {useStore} from "@state/base";
import proxy from "@common/lib/http-common";
import {deletePlannerNotesURL, editPlannerNotesURL, savePlannerNotesURL} from "@src/config/api-utils";
import {swalAttributes} from "@common/constants/common-attributes";
import {EventBus} from "@common/lib/event-bus";

const {getters, dispatch} = useStore()

const note = ref({
    id: '',
    note_title: '',
    description: '',
    start_date: '',
    end_date: '',
    note_color: '#ffb572',
    workspace_id: '',
    is_private: true,
    is_recurring: false,
    recurring_frequency: 'Daily',
    interval_value: 1,
    weekdays: [],
    end_condition: 'until',
    recurring_until_date: '',
    recurring_count: 1
})

const errors = ref({
    note_title: {
        error: false,
        message: ''
    },
    start_date: {
        error: false,
        message: ''
    },
    end_date: {
        error: false,
        message: ''
    },
    recurring_start: {
        error: false,
        message: ''
    },
    recurring_end: {
        error: false,
        message: ''
    },
    recurring_count: {
        error: false,
        message: ''
    },
    recurring_until_date: {
        error: false,
        message: ''
    }
})


export function usePlannerNotes() {
    const showModal = ref(false)
    const root = inject('root')
    const loader = ref(false)

    const colorOptions = [
        {name: "light_orange", value: "bg-[#ffb572]", hex: "#ffb572"},
        {name: "purple", value: "bg-[#864de9]", hex: "#864de9"},
        {name: "light_red", value: "bg-[#fea28b]", hex: "#fea28b"},
        {name: "dark_blue", value: "bg-[#0095f3]", hex: "#0095f3"},
        {name: "green", value: "bg-[#69c366]", hex: "#69c366"},
        {name: "pink", value: "bg-[#fa6ab6]", hex: "#fa6ab6"},
        {name: "blue", value: "bg-[#5cc6ff]", hex: "#5cc6ff"},
        {name: "light_blue", value: "bg[#7bd3ff]", hex: "#7bd3ff"},
        {name: "orange", value: "bg-[#e7af4d]", hex: "#e7af4d"},
        {name: "red", value: "bg-[#ff6462]", hex: "#ff6462"},
        {name: "blue2", value: "bg[#4da6ff]", hex: "#4da6ff"},
        {name: "lime", value: "bg-[#a4e786]", hex: "#a4e786"},
        {name: "indigo", value: "bg-[#7b8cff]", hex: "#7b8cff"}
    ];

    const dateOptions = () => {
        return {
            defaultValue: '',
            format: 'DD MMM YY',
        }
    }

    const getParsedDate = (date) => {
        const d = new Date(date) || new Date()
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    }

    const openModal = (editNote = null, isDuplicate = false) => {
        if (editNote) {
            note.value = {
                id: isDuplicate ? '' : editNote._id,
                note_title: editNote.note_title,
                description: editNote.description,
                start_date: new Date(editNote.start_date),
                end_date: new Date(editNote.end_date),
                note_color: editNote.note_color,
                workspace_id: getters.getActiveWorkspace._id,
                is_private: editNote?.is_private || false,
                is_recurring: editNote?.is_recurring || false,
                recurring_frequency: editNote?.recurring_frequency || 'Daily',
                interval_value: editNote?.interval_value || 1,
                weekdays: editNote?.weekdays || [],
                end_condition: editNote?.end_condition || 'until',
                recurring_until_date: editNote?.recurring_until_date ? new Date(editNote.recurring_until_date) : '',
                recurring_count: editNote?.recurring_count || 1,
                is_duplicate: isDuplicate
            }
        }
        root.$cstuModal.show('planner-notes-modal')
    }

    const closeModal = () => {
        root.$cstuModal.hide('planner-notes-modal')
        resetForm()
    }

    const saveNote = async () => {
        if (validateNote()) {
            // Implement your save logic here
            loader.value = true

            note.value.workspace_id = getters.getActiveWorkspace._id

            if(!note.value.id) {
                delete note.value.id
            }

            try {

                const requestURL = note.value.id ? editPlannerNotesURL : savePlannerNotesURL

                await proxy.post(requestURL, {
                    ...note.value,
                    start_date: getParsedDate(note.value.start_date),
                    end_date: getParsedDate(note.value.end_date || note.value.start_date),
                    recurring_until_date: note?.value?.recurring_until_date ? getParsedDate(note.value.recurring_until_date) : '',
                }).then(({data}) => {
                    const {status, message} = data
                    if (status) {
                        dispatch('toastNotification', {
                            message: !note.value.id ? 'Note saved successfully.' : 'Note updated successfully.',
                            type: 'success',
                        })
                        EventBus.$emit('refetch-plans')
                        loader.value = false
                        closeModal()
                    } else {
                        dispatch('toastNotification', {
                          message,
                          type: 'error',
                        })
                        loader.value = false
                    }
                })
            } catch (e) {

                let errorMessage = e?.response?.data?.message;
                if (e?.response?.data?.errors && typeof e?.response?.data?.errors === 'object') {
                    // Get the first error field's first message
                    const errorFields = Object.keys(e?.response?.data?.errors);
                    if (errorFields.length > 0) {
                        const firstErrorField = errorFields[0];
                        if (Array.isArray(e?.response?.data?.errors[firstErrorField]) && e?.response?.data?.errors[firstErrorField].length > 0) {
                            errorMessage = e?.response?.data?.errors[firstErrorField][0];
                        }
                    }
                }

                dispatch('toastNotification', {
                    message: errorMessage || e.message,
                    type: 'error'
                })
                loader.value = false
            }
        }
    }

    const deleteNote = async (item = null) => {
        const options = {
            ...swalAttributes(),
            modalClass: 'sweet-alert-confirmation fade sweet-alert-minimize-composer',
            okVariant: 'danger',
            hideHeader: false
        }
        const res = await root.$bvModal.msgBoxConfirm(
            'Are you sure you want to delete this note?',
            {
                title: 'Delete Note',
                ...options,
                cancelTitle: 'Cancel',
                okTitle: 'Delete',
            }
        )


        if (res === null || res === false) {
            return
        }
        try {
            let deleteNote = {}
            if(item?._id?.length) {
                deleteNote = {
                    id: item._id,
                    workspace_id: getters.getActiveWorkspace._id
                }
            } else {
                deleteNote = {
                    id: note.value.id,
                    workspace_id: getters.getActiveWorkspace._id
                }
            }

            await proxy.post(deletePlannerNotesURL, deleteNote).then(({data}) => {
                const {status} = data
                if (status) {
                    EventBus.$emit('refetch-plans')
                    closeModal()
                    dispatch('toastNotification', {
                        message: 'Note deleted successfully.',
                        type: 'success',
                    })

                }
            })
        } catch (e) {
            dispatch('toastNotification', {
                message: 'An error occurred while deleting note.',
                type: 'error'
            })
        }
    }

    const updateNoteField = (field, value) => {
        note.value[field] = value

        // Reset errors for this field
        if (errors.value[field]) {
            errors.value[field] = { error: false, message: '' }
        }
    }

    const handleRecurringChange = (value) => {
      // When toggling recurring off or on, reset fields
      if (value) {
        // Default values when turning on recurring
        note.value.recurring_frequency = 'Daily'
        note.value.weekdays = []
        note.value.end_condition = 'until'
        note.value.recurring_count = 1
        note.value.recurring_until_date = new Date(note.value.start_date)
      }
    }

    const toggleWeekday = (day) => {
      if (!note.value.weekdays) {
        note.value.weekdays = []
      }

      const index = note.value.weekdays.indexOf(day)
      if (index === -1) {
        // Add the day
        note.value.weekdays.push(day)
      } else {
        // Remove the day
        note.value.weekdays.splice(index, 1)
      }
    }

    const incrementOccurrences = () => {
        if (note.value.recurring_count <= 98) {
            note.value.recurring_count++;
        }
    }

    const decrementOccurrences = () => {
        if (note.value.recurring_count > 1) {
            note.value.recurring_count--;
        }
    }

    const decrementIntervalValue = () => {
        const value = parseInt(note.value.interval_value);
        if (value > 1) {
            updateNoteField('interval_value', value - 1);
        }
    }

    const incrementIntervalValue = () => {
        const value = parseInt(note.value.interval_value);
        if (value < 99) {
            updateNoteField('interval_value', value + 1);
        }
    }

    const getFrequencyLabel = () => {
        const value = parseInt(note.value.interval_value);
        const frequency = note.value.recurring_frequency || 'Daily';

        switch (frequency) {
            case 'Daily':
                return value === 1 ? 'day' : 'days';
            case 'Weekly':
                return value === 1 ? 'week' : 'weeks';
            case 'Monthly':
                return value === 1 ? 'month' : 'months';
            default:
                return '';
        }
    }

    const resetForm = () => {
        note.value = {
            id: '',
            note_title: '',
            description: '',
            start_date: '',
            end_date: '',
            note_color: '#ffb572',
            is_private: true,
            is_recurring: false,
            recurring_frequency: 'Daily',
            interval_value: 1,
            weekdays: [],
            end_condition: 'until',
            recurring_until_date: '',
            recurring_count: 1
        }
    }

    const validateNote = () => {
        let isValid = true;

        // Reset all errors first
        Object.keys(errors.value).forEach(key => {
            errors.value[key] = {
                error: false,
                message: ''
            }
        });

        // Validate note title
        if (!note.value.note_title) {
            errors.value.note_title = {
                error: true,
                message: 'Note title is required.'
            }
            isValid = false;

            dispatch('toastNotification', {
                message: 'Note title is required.',
                type: 'error',
            })
        }

        // Validate start date
        if (!note.value.start_date) {
            errors.value.start_date = {
                error: true,
                message: 'Start date is required.'
            }
            isValid = false;

            dispatch('toastNotification', {
                message: 'Start date is required.',
                type: 'error',
            })
        }

        if (!isValid) {
            return false;
        }

        return true;
    };

    const getRecurringNotesMessage = () => {
        // Format dates to display format
        const formatDate = (date) => {
            if (!date) return '';
            return new Date(date).toLocaleDateString('en-US', {
                day: 'numeric',
                month: 'short',
                year: 'numeric'
            });
        };

        const startDateFormatted = formatDate(note.value.start_date);
        const endDateFormatted = formatDate(note.value.end_date);
        const untilDateFormatted = formatDate(note.value.recurring_until_date);
        const intervalValue = parseInt(note.value.interval_value) || 1;

        // Get the day number from the start date for monthly recurrence
        const startDateObj = new Date(note.value.start_date);
        const startDay = startDateObj.getDate();

        const intervalText = intervalValue === 1 ? '' : `${intervalValue} `;

        // Get weekday names for weekly recurrence
        const getWeekdayNames = () => {
            const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
            if (!note.value.weekdays || !note.value.weekdays.length) {
                return 'no days selected';
            }

            // Sort weekdays to display in calendar order
            const sortedWeekdays = [...note.value.weekdays].sort((a, b) => a - b);
            const dayNames = sortedWeekdays.map(day => days[day]);

            if (dayNames.length === 1) {
                return dayNames[0];
            } else if (dayNames.length === 2) {
                return `${dayNames[0]} and ${dayNames[1]}`;
            } else {
                const lastDay = dayNames.pop();
                return `${dayNames.join(', ')} and ${lastDay}`;
            }
        };

        // Generate message based on frequency and end condition
        switch (note.value.recurring_frequency) {
            case 'Monthly':
                if (note.value.end_condition === 'until') {
                    return `Note will be repeated every ${intervalText}month${intervalValue > 1 ? 's' : ''} on day ${startDay}, until ${untilDateFormatted}`;
                } else {
                    return `Note will be repeated every ${intervalText}month${intervalValue > 1 ? 's' : ''} on day ${startDay}, ${note.value.recurring_count} time${note.value.recurring_count > 1 ? 's' : ''}`;
                }

            case 'Weekly':
                if (note.value.end_condition === 'until') {
                    return `Note will be repeated every ${intervalText}week${intervalValue > 1 ? 's' : ''} on ${getWeekdayNames()}, until ${untilDateFormatted}`;
                } else {
                    return `Note will be repeated every ${intervalText}week${intervalValue > 1 ? 's' : ''} on ${getWeekdayNames()}, ${note.value.recurring_count} time${note.value.recurring_count > 1 ? 's' : ''}`;
                }

            case 'Daily':
            default:
                if (note.value.end_condition === 'until') {
                    return `Note will be repeated every ${intervalText}day${intervalValue > 1 ? 's' : ''}, until ${untilDateFormatted}`;
                } else {
                    return `Note will be repeated every ${intervalText}day${intervalValue > 1 ? 's' : ''}, ${note.value.recurring_count} time${note.value.recurring_count > 1 ? 's' : ''}`;
                }
        }
    };

    /**
     * Automatically selects the Weekly frequency and adds the current start date's day of week
     */
    const selectWeeklyFrequency = () => {
        updateNoteField('recurring_frequency', 'Weekly')

        // Auto-select day from note's start date
        if (note.value.start_date) {
            // Initialize empty weekdays array if not already set
            if (!note.value.weekdays || !Array.isArray(note.value.weekdays)) {
                note.value.weekdays = []
            }

            const startDate = new Date(note.value.start_date)
            const dayOfWeek = startDate.getDay()

            // Make sure day of week isn't already added
            if (!note.value.weekdays.includes(dayOfWeek)) {
                // Call toggleWeekday - this will handle updating the array
                toggleWeekday(dayOfWeek)
            }
        }
    }

    /**
     * Handles input changes for recurring count, allows only numeric input
     */
    const handleRecurringCountInput = (event) => {
        const raw = event.target.value;
        const filtered = raw.replace(/[^0-9]/g, ''); // only digits

        // update view but don't validate range yet
        event.target.value = filtered;
        updateNoteField('recurring_count', filtered);
    }

    /**
     * Validates recurring count value when input loses focus
     */
    const validateRecurringCountOnBlur = (event) => {
        let value = parseInt(event.target.value);

        if (isNaN(value) || value < 1) {
            value = 1;
        } else if (value > 99) {
            value = 99;
        }

        updateNoteField('recurring_count', value);
        event.target.value = value;
    }

    /**
     * Handles input changes for interval value, allows only numeric input
     */
    const handleIntervalValueInput = (event) => {
        const raw = event.target.value;
        const filtered = raw.replace(/[^0-9]/g, ''); // only digits

        // Update view but don't validate range yet
        event.target.value = filtered;
        updateNoteField('interval_value', filtered);
    }

    /**
     * Validates interval value when input loses focus
     */
    const validateCountOnBlur = (event) => {
        let value = parseInt(event.target.value);

        if (isNaN(value) || value < 1) {
            value = 1;
        } else if (value > 99) {
            value = 99;
        }

        updateNoteField('interval_value', value);
        event.target.value = value;
    }

    return {
        showModal,
        note,
        colorOptions,
        loader,
        errors,
        openModal,
        closeModal,
        saveNote,
        updateNoteField,
        incrementOccurrences,
        decrementOccurrences,
        dateOptions,
        deleteNote,
        handleRecurringChange,
        toggleWeekday,
        getRecurringNotesMessage,
        selectWeeklyFrequency,
        handleRecurringCountInput,
        validateRecurringCountOnBlur,
        decrementIntervalValue,
        incrementIntervalValue,
        handleIntervalValueInput,
        getFrequencyLabel,
        validateCountOnBlur
    }
}
